import React, { useState } from "react";
import * as dmsService from "../../../services/dmsService";
import useDmsOptions from "../useDmsOptions";
import { DataTable, hint, linkBtn } from "../../Fin/gl/glUi";
import { DmsBadge, Panel, run } from "../sales/dmsUi";
import { AppDropdown, EmptyState, Field, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, Tabs, formatDate, formatDateTime, inputStyle, pageStyle, todayYmd, useLoad } from "../../../components/ui";

// After-sales care: recall campaigns, the service desk's reminder list (service due, warranty and
// contract expiry, recalls, win-back) and customer satisfaction (CSI) surveys with follow-ups.
const label = (s) => String(s || "").charAt(0) + String(s || "").slice(1).toLowerCase().replace(/_/g, " ");

/* ------------------------------ recalls ------------------------------ */

function RecallView({ id, onHide, onChanged }) {
  const { data: r, loading, error, reload } = useLoad(() => dmsService.getRecall(id), [id]);
  return (
    <Panel title={r ? `Recall ${r.campaign_number}` : "Recall"} onHide={onHide} width={760}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {r ? (
          <>
            <div className="d-flex justify-content-between mb-2">
              <div>
                <b>{r.title}</b>
                <div style={hint}>
                  From {formatDate(r.start_date)} · {label(r.status)}
                </div>
                {r.description ? <div style={{ fontSize: 14, marginTop: 6 }}>{r.description}</div> : null}
              </div>
              {r.status === "ACTIVE" ? <SecondaryButton onClick={() => window.confirm("Close this recall? Open reminders for it are dismissed.") && run(() => dmsService.closeRecall(id), () => {
                  reload();
                  onChanged();
                })}>Close recall</SecondaryButton> : null}
            </div>
            <DataTable
              rows={r.vehicles}
              rowKey={(v) => v.vin}
              columns={[
                { label: "VIN", render: (v) => v.vin },
                { label: "Status", render: (v) => <DmsBadge status={v.status === "COMPLETED" ? "DONE" : v.status} /> },
                { label: "Notified", render: (v) => (v.notified_at ? formatDate(v.notified_at) : "") },
                { label: "Done on", render: (v) => (v.ro_number ? `${v.ro_number} ${formatDate(v.completed_at)}` : "") },
              ]}
            />
          </>
        ) : null}
      </LoadState>
    </Panel>
  );
}

export function Recalls() {
  const { data, loading, error, reload } = useLoad(() => dmsService.listRecalls(), []);
  const opts = useDmsOptions(["makes", "labour-operations"]);
  const [open, setOpen] = useState(null);
  const [importing, setImporting] = useState(false);
  return (
    <div style={pageStyle}>
      <PageHeader title="Recalls" subtitle="Manufacturer recall campaigns and the vehicles they cover" actions={<PrimaryButton onClick={() => setImporting(true)}>Import recall</PrimaryButton>} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No recalls" />}
          columns={[
            { label: "Campaign", render: (r) => <b>{r.campaign_number}</b> },
            { label: "Title", render: (r) => r.title },
            { label: "From", render: (r) => formatDate(r.start_date) },
            { label: "Vehicles", num: true, render: (r) => Number(r.vins) },
            { label: "Open", num: true, render: (r) => Number(r.open_vins) },
            { label: "Notified", num: true, render: (r) => Number(r.notified) },
            { label: "Done", num: true, render: (r) => Number(r.completed) },
            { label: "Status", render: (r) => label(r.status) },
          ]}
        />
      </LoadState>
      {open ? <RecallView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
      <FormDrawer
        show={importing}
        title="Import a recall"
        width={620}
        initial={{ start_date: todayYmd() }}
        onHide={() => setImporting(false)}
        onDone={reload}
        onSave={(clean) => dmsService.importRecall(clean)}
        fields={[
          { key: "campaign_number", label: "Manufacturer's campaign number", required: true, max: 40, hint: "Importing the same number again adds VINs to it" },
          { key: "title", label: "Title", required: true, max: 200 },
          { key: "description", label: "What is wrong and the fix", type: "textarea", max: 2000 },
          { key: "make_id", label: "Make", type: "select", options: opts.makes || [] },
          { key: "labour_op_id", label: "Labour operation", type: "select", options: opts["labour-operations"] || [] },
          { key: "start_date", label: "From", type: "date", required: true },
          { key: "vins_text", label: "VINs (one per line, or separated by commas)", type: "textarea", rows: 8, required: true },
        ]}
      />
    </div>
  );
}

/* ------------------------------ reminders ------------------------------ */

const REASONS = ["SERVICE_DUE", "WARRANTY_EXPIRY", "CONTRACT_EXPIRY", "RECALL", "RETENTION"];

export function ServiceReminders() {
  const [status, setStatus] = useState("OPEN");
  const [reason, setReason] = useState("");
  const [q, setQ] = useState("");
  const { data, loading, error, reload } = useLoad(() => dmsService.listReminders({ status, reason: reason || undefined, q: q || undefined }), [status, reason, q]);
  const [working, setWorking] = useState(null);
  return (
    <div style={pageStyle}>
      <PageHeader title="Service Reminders" subtitle="Who to call and why: service due, expiring warranties and contracts, recalls, win-back" actions={<SecondaryButton onClick={() => run(() => dmsService.generateReminders(), reload)}>Refresh the list</SecondaryButton>} />
      <div className="d-flex flex-wrap gap-2 mb-3">
        <input className="form-control" style={{ ...inputStyle, width: 240 }} placeholder="VIN, customer, phone" aria-label="Search reminders" value={q} onChange={(e) => setQ(e.target.value)} />
        <div style={{ width: 180 }}>
          <AppDropdown id="rem-st" value={status} options={["OPEN", "CONTACTED", "BOOKED", "DECLINED", "NO_ANSWER", "DISMISSED", "ALL"].map((s) => ({ label: label(s), value: s }))} onChange={(e) => setStatus(e.value)} />
        </div>
        <div style={{ width: 200 }}>
          <AppDropdown id="rem-reason" value={reason} options={[{ label: "Every reason", value: "" }, ...REASONS.map((s) => ({ label: label(s), value: s }))]} onChange={(e) => setReason(e.value)} />
        </div>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setWorking(r)}
          empty={<EmptyState title="Nobody to call" />}
          columns={[
            { label: "Due", render: (r) => (r.due_date ? formatDate(r.due_date) : "") },
            { label: "Why", render: (r) => `${label(r.reason)}${r.reason_ref ? ` · ${r.reason_ref}` : ""}` },
            { label: "VIN", render: (r) => r.vin },
            { label: "Customer", render: (r) => r.customer_name || "Unknown owner" },
            { label: "Phone", render: (r) => r.contact_phone || "" },
            { label: "Km due", num: true, render: (r) => r.due_km || "" },
            { label: "Status", render: (r) => label(r.status) },
            { label: "Last contact", render: (r) => (r.contacted_at ? `${formatDateTime(r.contacted_at)} ${r.channel ? `(${label(r.channel)})` : ""}` : "") },
          ]}
        />
      </LoadState>
      <FormDrawer
        show={!!working}
        title={working ? `${working.customer_name || working.vin}: ${label(working.reason)}` : ""}
        initial={working ? { outcome_status: "CONTACTED", channel: working.channel || "CALL", outcome: working.outcome || "" } : {}}
        onHide={() => setWorking(null)}
        onDone={reload}
        onSave={(clean) => dmsService.workReminder(working.id, clean)}
        fields={[
          { key: "outcome_status", label: "Result", type: "select", required: true, options: ["CONTACTED", "BOOKED", "DECLINED", "NO_ANSWER", "DISMISSED"].map((s) => ({ label: label(s), value: s })) },
          { key: "channel", label: "How", type: "select", options: ["CALL", "WHATSAPP", "SMS", "EMAIL"].map((s) => ({ label: label(s), value: s })) },
          { key: "outcome", label: "Notes", type: "textarea", max: 500 },
        ]}
      />
    </div>
  );
}

/* ------------------------------ CSI ------------------------------ */

function AnswerPanel({ id, onHide, onDone }) {
  const { data: r, loading, error, reload } = useLoad(() => dmsService.getCsi(id), [id]);
  const [values, setValues] = useState({});
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (noResponse) => {
    setBusy(true);
    const res = await run(() => dmsService.csiAction(id, "answer", noResponse ? { no_response: true } : { answers: r.questions.map((q) => ({ question_id: q.id, value: values[q.id] ?? null })), comment }));
    setBusy(false);
    if (res) {
      onDone();
      onHide();
    }
  };
  return (
    <Panel
      title={r ? `${r.survey ? r.survey.name : "Survey"} · ${r.doc_number || ""}` : "Survey"}
      onHide={() => !busy && onHide()}
      width={680}
      footer={
        r && r.status === "PENDING" ? (
          <>
            <SecondaryButton onClick={() => save(true)} disabled={busy}>
              No response
            </SecondaryButton>
            <PrimaryButton onClick={() => save(false)} disabled={busy}>
              Save answers
            </PrimaryButton>
          </>
        ) : null
      }
    >
      <LoadState loading={loading} error={error} onRetry={reload}>
        {r ? (
          <>
            <div style={{ ...hint, marginBottom: 12 }}>
              {r.customer_name} {r.contact_phone ? `· ${r.contact_phone}` : ""} · {r.employee_name ? `rates ${r.employee_name}` : ""}
            </div>
            {r.questions.map((q, i) => {
              // Answers are stored in question order (ids are re-encrypted per response, so never compared).
              const given = (r.answers || [])[i];
              return (
                <div key={q.id} className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 14 }}>
                  <div style={{ flex: 1 }}>{q.question}</div>
                  {r.status === "PENDING" ? (
                    <div className="btn-group btn-group-sm" role="group" aria-label={q.question}>
                      {Array.from({ length: r.survey ? r.survey.scale_max : 5 }, (_, i) => i + 1).map((n) => (
                        <button key={n} type="button" className={`btn ${values[q.id] === n ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setValues({ ...values, [q.id]: n })}>
                          {n}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <b>{given ? given.value : "—"}</b>
                  )}
                </div>
              );
            })}
            {r.status === "PENDING" ? (
              <Field label="Customer's comment">
                <textarea className="form-control" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
              </Field>
            ) : (
              <div style={{ fontSize: 14 }}>
                Score <b>{r.score}</b> {r.comment ? `· "${r.comment}"` : ""}
                {r.resolution ? <div style={hint}>Follow-up: {r.resolution}</div> : null}
              </div>
            )}
          </>
        ) : null}
      </LoadState>
    </Panel>
  );
}

export function CsiSurveys() {
  const [tab, setTab] = useState("pending");
  const filters = { pending: { status: "PENDING" }, followups: { escalation: "OPEN" }, all: {} }[tab] || {};
  // The list and the Scores report answer in different shapes, so each has its own load: sharing one
  // showed the previous tab's list to the report for a render and crashed it.
  const isReport = tab === "report";
  const listLoad = useLoad(() => dmsService.listCsi(filters), [tab], { enabled: !isReport });
  const reportLoad = useLoad(() => dmsService.csiReport(), [tab], { enabled: isReport });
  const { data, loading, error, reload } = isReport ? reportLoad : listLoad;
  const [open, setOpen] = useState(null);
  const [resolving, setResolving] = useState(null);
  return (
    <div style={pageStyle}>
      <PageHeader title="Customer Satisfaction" subtitle="Surveys after each delivery and service visit; low scores are followed up" />
      <Tabs
        tabs={[
          { key: "pending", label: "To call" },
          { key: "followups", label: "Follow-ups" },
          { key: "all", label: "All" },
          { key: "report", label: "Scores" },
        ]}
        active={tab}
        onChange={setTab}
      />
      <LoadState loading={loading} error={error} onRetry={reload}>
        {tab === "report" ? (
          data ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
              <DataTable rows={data.by_month} rowKey={(r) => `${r.month}-${r.doc_type}`} minWidth={300} empty={<EmptyState title="No answers yet" />} columns={[{ label: "Month", render: (r) => r.month }, { label: "After", render: (r) => (r.doc_type === "RO" ? "Service" : "Purchase") }, { label: "Surveys", num: true, render: (r) => r.n }, { label: "Score", num: true, render: (r) => r.score }]} />
              <DataTable rows={data.by_person} rowKey={(r) => `${r.name}-${r.doc_type}`} minWidth={300} empty={<EmptyState title="No answers yet" />} columns={[{ label: "Person", render: (r) => r.name }, { label: "After", render: (r) => (r.doc_type === "RO" ? "Service" : "Purchase") }, { label: "Surveys", num: true, render: (r) => r.n }, { label: "Score", num: true, render: (r) => r.score }, { label: "Follow-ups", num: true, render: (r) => r.escalations }]} />
            </div>
          ) : null
        ) : (
          <DataTable
            rows={data || []}
            onRowClick={(r) => setOpen(r.id)}
            empty={<EmptyState title={tab === "followups" ? "No open follow-ups" : "Nothing here"} />}
            columns={[
              { label: "After", render: (r) => `${r.doc_type === "RO" ? "Service" : "Purchase"} ${r.doc_number || ""}` },
              { label: "Customer", render: (r) => r.customer_name },
              { label: "Phone", render: (r) => r.contact_phone || "" },
              { label: "Rates", render: (r) => r.employee_name || "" },
              { label: "Score", num: true, render: (r) => (r.score === null ? "" : Number(r.score)) },
              { label: "Status", render: (r) => label(r.status) },
              {
                label: "",
                render: (r) =>
                  r.escalation === "OPEN" ? (
                    <button
                      type="button"
                      style={linkBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        setResolving(r);
                      }}
                    >
                      Close follow-up
                    </button>
                  ) : null,
              },
            ]}
          />
        )}
      </LoadState>
      {open ? <AnswerPanel id={open} onHide={() => setOpen(null)} onDone={reload} /> : null}
      <FormDrawer
        show={!!resolving}
        title="Close the follow-up"
        onHide={() => setResolving(null)}
        onDone={reload}
        onSave={(clean) => dmsService.csiAction(resolving.id, "resolve", clean)}
        fields={[{ key: "resolution", label: "What was done for the customer", type: "textarea", required: true, max: 1000 }]}
      />
    </div>
  );
}
